<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div
    v-else-if="!hasPermission"
    class="m-3"
  >
    <access-denied/>
  </div>
  <div
    v-else
  >
    <div class="d-flex justify-content-between border-bottom mb-3">
      <ul class="nav nav-custom">
        <li class="nav-item">
          <a class="nav-link active">
            {{ $t('All') }}
            <span class="badge rounded-pill bg-primary">
              {{ datatable.total }}
            </span>
          </a>
        </li>
      </ul>
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          aria-describedby="search-addon"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text bg-white text-muted"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>
      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="datatable.headers($t)"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      buttons-pagination
      :body-item-class-name="bodyItemClassNameFunction"
      table-class-name='customize-table'
    >
      <template #header-created_by="header">
        <span class="w-100 text-end">
          {{ header.text }}
        </span>
      </template>
      <template #item-name="item">
        {{ $t(item.name) }}
      </template>
      <template #item-status="item">
        <span v-if="item.status == 'Open'" class="badge bg-success">
          <i class="fas fa-check-circle me-1" />
          {{ $t(item.status) }}
        </span>
        <span v-else class="badge bg-danger">
          <i class="fas fa-times-circle me-1" />
          {{ $t(item.status) }}
        </span>
      </template>
      <template #item-count_orders="item">
        <span
          class="text-danger fw-bold"
          v-if="item.count_orders"
        >
          <i class="fa-solid fa-fire text-danger me-1"/>
          {{ item.count_orders + ' ' + $t('Orders') }}
        </span>
        {{ !item.count_orders? '-': '' }}
      </template>
      <template #item-action="item">
        <div
          class="icon-link custom-actions"
        >
          <!-- ล้างของค้าง: ร้านที่ขายผ่าน POS อย่างเดียวจะไม่มีใครกดปิดงานบนจอครัวเลย
               ออเดอร์เลยค้างสะสม — ปุ่มนี้ปิดให้หมดในครั้งเดียวโดยไม่ต้องเข้าจอครัว -->
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
            @click="confirmCompletePending(item)"
            v-if="item.count_orders"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-outline-danger btn-sm btn-icon"
              :disabled="completing === item.id"
            >
              <i
                class="fa-solid"
                :class="completing === item.id ? 'fa-spinner fa-spin' : 'fa-broom'"
              />
            </button>
            <template #content>
              <div>{{ $t('Complete pending orders') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
            @click="enterKDS(item.id)"
            v-if="item.status == 'Open'"
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-success btn-sm btn-icon"
            >
              <i class="fa-solid fa-arrow-right" />
            </button>
            <template #content>
              <div>{{ $t('Enter KDS') }}</div>
            </template>
          </popper>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
            @click="enterKDS(item.id)"
            v-else
          >
            <button
              type="button"
              data-bs-toggle="popover"
              data-bs-placement="top"
              class="btn btn-primary btn-sm btn-icon"
            >
              <i class="fa-solid fa-arrow-right" />
            </button>
            <template #content>
              <div>{{ $t('Enter KDS') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>
  </div>
</template>
<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import PerPage from '@/plugins/PerPage.vue';
import FieldsDef from "./assets/template/datatable/FieldsDef";
import { KDSRegisters } from '@/services/endpoint/kds.service';
import moment from "moment";
import numeral from 'numeral';
import Popper from "vue3-popper";
import { distance } from '@/store/date-fns';

export default {
  data() {
    return {
      params: {
        limit: 10,
        page: 1,
        search: ''
      },
      pageLoading: false,
      hasPermission: false,
      completing: 0,      // branch id ที่กำลังล้างของค้างอยู่ (0 = ว่าง)
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10
        }
      },
      offcanvasShiftInfo: {
        open: false,
        id: 0
      },
      offcanvasFinalizeCloseShiftForm: {
        open: false,
        id: 0
      },
      offcanvasOpenShiftForm: {
        open: false,
        id: 0
      },
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    PerPage,
    Vue3EasyDataTable,
    Popper
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    enterKDS(id) {
      // ไปหน้าเลือกสถานีก่อน (เลือก KDS ไหน / ทุกหมวด) — ชื่อสาขาดึงจาก API ฝั่งหน้านั้น
      this.$router.push({ name: 'kds.station-select', params: { branchId: id } });
    },
    confirmCompletePending(item) {
      if (this.completing) return;
      this.$swal({
        icon: 'warning',
        iconColor: '#d97706',
        title: this.$t('Complete pending orders') + '?',
        html: '<b>' + item.name + '</b><br><br>'
          + this.$t('Mark all pending orders on this branch KDS as completed')
          + ' (' + this.formatNumber(item.count_orders) + ' ' + this.$t('Orders') + ')'
          + '<br><br><span class="text-danger">' + this.$t('This action cannot be undone') + '</span>',
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: '<i class="fa-solid fa-broom me-1"></i>' + this.$t('Complete pending orders'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger me-2',
          cancelButton: 'btn btn-secondary',
        },
        focusConfirm: false,
      }).then((result) => {
        if (result.isConfirmed) this.completePending(item.id);
      });
    },
    async completePending(id) {
      this.completing = id;
      await KDSRegisters.completePendingOrders(id).then(res => {
        // ใช้จำนวนที่ BE ปิดจริง ไม่ใช่เลขที่ตารางถืออยู่ตอนกด — ระหว่างยืนยันอาจมีออเดอร์ใหม่เข้ามา
        const data = res.data.data;
        this.$toast.success(
          this.$t('Pending orders completed') + ' — '
          + this.formatNumber(data.orders) + ' ' + this.$t('Orders')
        );
        this.fetchData();
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err.response.data.message));
        }
      });
      this.completing = 0;
    },
    dateDistance(value) {
      return distance(value);
    },
    bodyItemClassNameFunction(column, rowNumber) {
      let className = 'align-top py-1 px-2';
      const columnCenter = [];
      const columnEnd = ['options_count', 'created_by'];
      if (columnCenter.includes(column)) {
        className += ' text-center';
      }
      if (columnEnd.includes(column)) {
        className += ' text-end';
      }
      return className;
    },
    async fetchData() {
      this.datatable.loading = true;
      var queryString = '?' + $.param(this.params);
      await KDSRegisters.list(queryString).then(res => {
        this.datatable.items = res.data.data;
        this.datatable.total = res.data.meta.total;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });

      this.datatable.loading = false;
      return;
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatTimeNotSec(value) {
      return moment(value).format('HH:mm');
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
  },
  watch: {
    "datatable.serverOptions" (nval, oval) {
      delete this.params.sort;
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      if (nval.sortBy && nval.sortType) this.params.sort = nval.sortBy + "|" + nval.sortType;
      this.fetchData();
    }
  }
}
</script>
